<template lang="html">
  <!--
  Component <base-modal> for modals
  - Accepts the following properties:
  [maxHeight, maxWidth]

  - Accepts callback property:
  [on-base-modal-close]

  @example

    <base-modal
      ref="baseModal"
      v-if="displayModal"
      :maxHeight="maxHeight"
      :maxWidth="maxWidth"
      :show="displayModal"
      @on-base-modal-close="cancelModal"
    >
      //  header
      <template v-slot:header>
        <my-custom-header />
      </template>

      // content
      <some-custom-content />

      ///// footer
      <template v-slot:footer>
        <some-custom-footer />
      </template>
    </base-modal>
  -->

  <section>
    <div class="m-base-modal" @keydown.esc.stop="onCloseModal">
      <div ref="modalContainer" class="m-modal-container">
        <div class="m-close-modal-icon">
          <svg id="closeModal" v-on:click.stop.prevent="onCloseModal">
            <use :xlink:href="getUtilityIcon('close')"></use>
          </svg>
        </div>
        <header ref="header" v-if="hasHeader()">
          <slot name="header"></slot>
        </header>
        <main ref="main" :class="mainClass"><slot></slot></main>
        <footer ref="footer" v-if="hasFooter()">
          <slot name="footer"></slot>
        </footer>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";

  export default  {
    name: 'base-modal',
    props: {
      maxHeight: {
        type: Number
      },
      maxWidth: {
        type: Number
      },
      noPadding: Boolean,
      noMargin: Boolean
    },
    computed: {
      mainClass() {
        return {
          "no-padding": this.noPadding,
          "no-margin": this.noMargin
        };
      }
    },
    watch: {
      maxHeight: function() {
        this.setMaxHeight();
      },
      maxWidth() {
        this.setMaxWidth();
      }
    },
    mounted() {
      this.setMaxHeight();
      this.setMaxWidth();
    },
    methods: {
      setMaxHeight() {
      if(this.maxHeight) {
        let maxHeight = this.maxHeight;
        if(this.$refs.header) {
          maxHeight -= this.$refs.header.clientHeight + 2;
        }
        if(this.$refs.footer) {
          maxHeight -= this.$refs.footer.clientHeight + 2;
        }
        if(this.$refs.main) {
          this.$refs.main.style.height = maxHeight + "px";
        }
      }
      },
      setMaxWidth() {
        if(this.maxWidth) {
          this.$refs.modalContainer.style.maxWidth = this.maxWidth + "px";
        }
      },
      getUtilityIcon(iconName) {
        return iconsUri.getUtilityUrl(iconName);
      },
      onCloseModal() {
        /**
         * Triggers when model value changes
         */
        const EVENT = "on-base-modal-close";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          this.$emit(EVENT, false);
        }
      },
      hasHeader() {
        return !!this.$slots.header;
      },
      hasFooter() {
        return !!this.$slots.footer;
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  $modal-background-color: #fff;
  .m-base-modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    font-size: 0.8125rem;
    z-index: 9000;
    background-color: rgba(8, 7, 7, 0.6);
    padding: 0 30px;
    overflow-y: auto;

    header,
    main,
    footer {
      box-sizing: border-box;
    }
    header {
      position: relative;
      border-bottom: 2px solid #dddbda;
      padding: 1rem;
      text-align: center;
      font-size: 1.25rem;
      line-height: 1.25;
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
    }
    main {
      padding: 1rem;
      overflow: auto;
      min-height: 50px;
    }
    footer {
      border-top: 2px solid #dddbda;
      text-align: right;
      padding: 0.75rem 1rem;
      background-color: #f3f2f2;
      box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
      border-bottom-right-radius: 0.25rem;
      border-bottom-left-radius: 0.25rem;
    }

    .m-modal-container {
      background-color: $modal-background-color;
      min-width: 700px;
      min-height: 40px;
      position: relative;
      top: 10%;
      margin: 0 auto;
      border-radius: 0.25rem;
      box-sizing: border-box;
      max-width: fit-content;
    }
    .m-close-modal-icon {
      svg {
        fill: #fff;
        width: 1.5rem;
        height: 1.5rem;
        position: absolute;
        top: -2.5rem;
        right: -0.5rem;
        cursor: pointer;
      }
      &:active {
        svg {
          fill: hsla(0, 0%, 100%, 0.5);
        }
      }
    }
    .no-padding {
      padding: 0;
    }
    .no-margin {
      margin: 0;
    }
  }
</style>
